/**
 * Viewer preview overlay — flips the editor viewport to the REAL viewer2 on
 * the same page, so you can verify "what ships is what I authored" without
 * leaving the editor.
 *
 * It embeds viewer2 in an <iframe> (the two are separate apps on separate
 * origins — editor :3000, viewer :5174 in dev — so an iframe is the clean
 * boundary: the iframe runs the actual shipping renderer, fully isolated).
 *
 * The viewer loads scenes by URL (?scene=<name>), so the scene you preview
 * must already be served by the viewer — i.e. exported into
 * webpages/viewer2/public/scenes/<name>/ (locally) or uploaded (prod). This overlay
 * doesn't do the export; it just points the real viewer at a scene name.
 * The header has an editable scene-name field + reload so you can re-check
 * after re-exporting.
 *
 * Configure the viewer origin via the VIEWER_ORIGIN constant (or the
 * `?viewerOrigin=` URL param on the editor) — dev localhost vs prod.
 */

import { Container, Label, Button, TextInput } from '@playcanvas/pcui';

import type { Events } from '../../events';

// Dev default. Override with ?viewerOrigin=https://viewer.olloport.com on the
// editor URL when checking against the deployed viewer.
const DEFAULT_VIEWER_ORIGIN = 'http://localhost:5174';

const slugify = (s: string): string =>
    (s ?? '')
        .replace(/\.[^.]+$/, '')                 // drop extension
        .replace(/[^a-zA-Z0-9_-]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase() || 'scene';

class ViewerPreview extends Container {
    private iframe: HTMLIFrameElement;
    private sceneInput: TextInput;
    private statusLabel!: Label;
    private viewerOrigin: string;
    private events: Events;
    private _visible = false;
    private _statusTimer: number | null = null;
    // Which scene slug the iframe currently has loaded, and whether its page
    // has finished loading. Lets re-opening the preview reuse the already-loaded
    // splat (live camera jump) instead of reloading it from scratch.
    private loadedSlug: string | null = null;
    private iframeReady = false;

    constructor(events: Events, args = {}) {
        args = {
            ...args,
            id: 'viewer-preview',
            class: 'viewer-preview',
            hidden: true
        };
        super(args);

        this.events = events;
        const urlOrigin = new URLSearchParams(location.search).get('viewerOrigin');
        this.viewerOrigin = urlOrigin || DEFAULT_VIEWER_ORIGIN;

        // ── Header bar ────────────────────────────────────────────────────
        const header = new Container({ class: 'viewer-preview-header' });

        header.append(new Label({ class: 'viewer-preview-title', text: 'VIEWER PREVIEW' }));

        const sceneLabel = new Label({ class: 'viewer-preview-scene-label', text: 'scene:' });
        header.append(sceneLabel);

        this.sceneInput = new TextInput({ class: 'viewer-preview-scene', value: '' });
        header.append(this.sceneInput);

        // Transient status: "publishing…" / "published N files" / errors.
        this.statusLabel = new Label({ class: 'viewer-preview-status', text: '' });
        header.append(this.statusLabel);

        const reload = new Button({ class: 'viewer-preview-btn', text: 'Publish & reload' });
        reload.on('click', () => void this.fullLoad(this.sceneInput.value));
        header.append(reload);

        const openTab = new Button({ class: 'viewer-preview-btn', text: 'Open tab ↗' });
        openTab.on('click', async () => {
            await this.publish(this.sceneInput.value);
            window.open(this.sceneUrl(this.sceneInput.value), '_blank');
        });
        header.append(openTab);

        const close = new Button({ class: ['viewer-preview-btn', 'viewer-preview-close'], text: '✕ Back to editor' });
        close.on('click', () => events.fire('viewerPreview.hide'));
        header.append(close);

        this.append(header);

        // ── Iframe ────────────────────────────────────────────────────────
        this.iframe = document.createElement('iframe');
        this.iframe.className = 'viewer-preview-iframe';
        this.iframe.setAttribute('allow', 'fullscreen; xr-spatial-tracking');
        // The viewer page has loaded (its module boot, not the splat decode);
        // safe to start sending it live camera/pause messages.
        this.iframe.addEventListener('load', () => { this.iframeReady = true; });
        const iframeWrap = new Container({ class: 'viewer-preview-iframe-wrap' });
        iframeWrap.dom.appendChild(this.iframe);
        this.append(iframeWrap);

        // Stop pointer events bubbling to the editor canvas. Without this the
        // editor's camera controls capture the pointer and EAT clicks on the
        // header buttons — "Back to editor" flashed but never closed. Same
        // guard the floating panels use (see OVERVIEW §8 / lighting-panel.ts).
        // (Events over the <iframe> stay inside it, so the viewer is unaffected.)
        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((evt) => {
            this.dom.addEventListener(evt, (e: Event) => e.stopPropagation());
        });

        // ── Events ────────────────────────────────────────────────────────
        events.on('viewerPreview.show', (sceneName?: string) => this.show(events, sceneName));
        events.on('viewerPreview.hide', () => this.hide(events));
        events.on('viewerPreview.toggle', () => {
            if (this._visible) this.hide(events);
            else this.show(events);
        });
    }

    private sceneUrl(name: string): string {
        const scene = name || 'scene';
        return `${this.viewerOrigin}/?scene=${encodeURIComponent(scene)}&embed=1`;
    }

    /**
     * Publish the current editor scene to the viewer dev server. Returns ok +
     * whether any heavy asset (splat / LOD / model) changed — only scene.json
     * changing means we can live-update the camera instead of reloading.
     */
    private async publish(name: string): Promise<{ ok: boolean; assetsChanged: boolean }> {
        const slug = slugify(name);
        this.setStatus('publishing…');
        const res = await this.events.invoke('scene.publishToViewer', {
            name: slug,
            origin: this.viewerOrigin
        }) as { ok: boolean; files?: number; total?: number; assetsChanged?: boolean; error?: string } | undefined;
        if (res?.ok) {
            // "shipped N / M" — N changed files transferred, M total in the scene.
            const shipped = res.files ?? 0;
            this.setStatus(
                res.total != null ? `published ${shipped}/${res.total} files` : `published ${shipped} files`,
                2500
            );
            return { ok: true, assetsChanged: res.assetsChanged ?? true };
        }
        this.setStatus(`publish failed: ${res?.error ?? 'unknown'}`, 8000);
        return { ok: false, assetsChanged: true };
    }

    /**
     * Full (re)load: publish, then point the iframe at a fresh copy. The
     * expensive path — the viewer re-decodes the whole splat. Used on first
     * open, when the scene name changes, when a heavy asset changed, or via the
     * explicit "Publish & reload" button.
     */
    private async fullLoad(name: string) {
        const slug = slugify(name);
        this.iframeReady = false;
        await this.publish(name);
        // Load regardless: on a publish failure the viewer surfaces its own
        // error, and any previously-published copy is still worth seeing.
        this.iframe.src = this.sceneUrl(slug);
        this.loadedSlug = slug;
    }

    /** Send the authored start pose to the already-loaded viewer (no reload). */
    private pushCamera() {
        const pose = this.currentPose();
        if (!pose || !this.iframe.contentWindow) return;
        this.iframe.contentWindow.postMessage({
            type: 'olloport:setCamera',
            position: pose.position,
            target: pose.target,
            fov: pose.fov
        }, this.viewerOrigin);
    }

    /** Pause/resume the hidden preview's render loop (keeps the scene resident). */
    private setActive(active: boolean) {
        this.iframe.contentWindow?.postMessage({ type: 'olloport:setActive', active }, this.viewerOrigin);
    }

    /**
     * The pose the exported scene.json would spawn at: the explicit "starting
     * view" if set, else the live editor viewport. Normalised to [x,y,z] arrays.
     */
    private currentPose(): { position: number[]; target: number[]; fov: number } | null {
        const sp = this.events.invoke('camera.getStartPose') as
            { position: number[]; target: number[]; fov: number } | null | undefined;
        if (sp) return { position: sp.position, target: sp.target, fov: sp.fov };
        const lp = this.events.invoke('camera.getPose') as
            { position: { x: number; y: number; z: number }; target: { x: number; y: number; z: number }; fov: number } | null | undefined;
        if (lp) {
            return {
                position: [lp.position.x, lp.position.y, lp.position.z],
                target: [lp.target.x, lp.target.y, lp.target.z],
                fov: lp.fov
            };
        }
        return null;
    }

    private setStatus(text: string, clearAfterMs?: number) {
        this.statusLabel.text = text;
        if (this._statusTimer !== null) {
            clearTimeout(this._statusTimer);
            this._statusTimer = null;
        }
        if (clearAfterMs) {
            this._statusTimer = window.setTimeout(() => {
                this.statusLabel.text = '';
                this._statusTimer = null;
            }, clearAfterMs);
        }
    }

    private show(events: Events, sceneName?: string) {
        // Default the scene name to the current document's slug if not given
        // and the field is empty.
        if (sceneName) {
            this.sceneInput.value = sceneName;
        } else if (!this.sceneInput.value) {
            const docName = (events.invoke('doc.name') as string) ?? 'scene';
            this.sceneInput.value = slugify(docName);
        }
        this._visible = true;
        this.hidden = false;
        void this.refresh(this.sceneInput.value);
        events.fire('viewerPreview.visible', true);
    }

    /**
     * Bring the preview up to date as cheaply as possible. If the same scene is
     * already loaded in the iframe, resume it and either live-jump the camera
     * (only scene.json changed) or reload (a heavy asset changed) — so the
     * common "tweak the start view, look again" loop is instant instead of a
     * full splat re-decode. First open / new scene → full load.
     */
    private async refresh(name: string) {
        const slug = slugify(name);
        if (this.loadedSlug === slug && this.iframeReady) {
            this.setActive(true);                 // un-pause the kept-alive viewer
            const { ok, assetsChanged } = await this.publish(name);
            if (ok && assetsChanged) {
                await this.fullLoad(name);        // splat/LOD/model changed → reload
            } else {
                this.pushCamera();                // instant — no reload
                this.setStatus('camera updated (Reload for splat/lighting)', 2500);
            }
        } else {
            await this.fullLoad(name);
        }
    }

    private hide(events: Events) {
        this._visible = false;
        this.hidden = true;
        // Keep the iframe loaded (so re-opening is instant) but pause its render
        // loop so the editor + preview don't both drive the GPU at once. The
        // scene stays resident in the viewer's memory.
        this.setActive(false);
        events.fire('viewerPreview.visible', false);
    }
}

export { ViewerPreview };
